export type BackendStatus =
  | "draft"
  | "sent"
  | "created"
  | "viewed"
  | "paid"
  | "partially_paid"
  | "overdue"
  | "expired"
  | "email_failed"
  | "payment_failed"
  // Set when the customer paid with an async method (bank debit/transfer/
  // voucher) and Stripe has not yet confirmed the funds. Non-terminal,
  // hands-off, view-only; resolves to paid/payment_failed via webhook.
  | "payment_pending"
  | "voided"

/**
 * The payment mode an invoice was created with. ``"full"`` is the
 * one-shot Checkout flow; ``"installments"`` is the off-session
 * auto-charge plan; ``"deposit"`` is the link-based 50/50 deposit +
 * balance flow. Derived backend-side from the plan FKs and surfaced as
 * a computed field on every invoice output (list row + detail).
 */
export type PaymentMode = "full" | "installments" | "deposit"

/**
 * Create-form payment-mode selection. Superset of the backend
 * {@link PaymentMode}: adds the UI-only ``"on_call"`` channel (MOTO
 * collection on a phone call). ``"on_call"`` is never sent to the API —
 * it maps to a one-time ``"full"`` invoice charged immediately instead of
 * emailed (on-call-payment design §1). Only the create form uses this; the
 * API in/out types keep the narrow {@link PaymentMode}.
 */
export type CreateInvoicePaymentMode = PaymentMode | "on_call"

// Branded literal so call sites that need to special-case the
// "first delivery never reached the customer" state share a single
// import — see actions.ts for why this state routes through /send/.
export const EMAIL_FAILED_STATUS = "email_failed" as const satisfies BackendStatus

// Branded literal for the "issued without emailing the customer" state
// (create-invoice-without-sending-email). Call sites special-case it to
// offer the "Send email" action, which routes through /send/ exactly like
// EMAIL_FAILED_STATUS does.
export const CREATED_STATUS = "created" as const satisfies BackendStatus

export type StripeAccount = "US" | "UK"

export interface CreatedByOut {
  id: number
  email: string
  display_name: string
}

/**
 * One course on a rep list/detail response (multiple-courses-in-invoice).
 *
 * The per-course snapshot the edit form re-hydrates from: ``unit_price``
 * is the **per-seat** price in the invoice's issue currency (what the edit
 * form seeds into the row's Price input) and ``amount`` is the per-course
 * TOTAL (= ``round(unit_price × seats)``, the whole-order line total).
 * ``base_currency`` is the course's pre-conversion currency, so the form
 * can re-send the row identity-safe (re-converting a base==issue pair is
 * a no-op). The top-level ``course_code`` / ``course_name`` / ``course_id``
 * on the invoice stay the single-label primary (position-0) mirror; this
 * is the full per-course breakdown. Optional for fixture back-compat —
 * runtime always populates it.
 */
export interface InvoiceLineItemOut {
  position: number
  course_id: string
  course_code: string
  /**
   * Effective customer-facing name — the SR override or, when un-overridden,
   * the Planna snapshot. What the customer sees on the pay page / PDF / email.
   */
  course_name: string
  /**
   * Pristine Planna snapshot — the baseline the "Reset to default" control
   * restores. Equal to ``course_name`` for an un-overridden row. Optional
   * only for fixture back-compat — runtime always populates it.
   */
  course_name_default?: string
  cohort_id: number | null
  course_start_date: string
  /**
   * Per-seat unit price in the issue currency. Seeds the edit form's Price
   * input (the field is the per-seat unit; the backend derives the total).
   * Optional only for fixture back-compat — runtime always populates it.
   */
  unit_price?: string
  /** Per-course TOTAL in the issue currency (= ``round(unit_price × seats)``). */
  amount: string
  base_currency: string
  market_name: string
  instructor_name: string
  instructor_photo_url: string
  full_price: string | null
  tier_index: number | null
}

export interface InvoiceOut {
  id: string
  email: string
  first_name: string
  last_name: string
  amount: string
  /**
   * The discount-inclusive price the customer actually pays: gross
   * `amount` minus the full standard + named discount cascade, computed
   * server-side via `compute_net_amount`. Equals `amount` when nothing is
   * discounted. Optional for fixture back-compat (runtime always
   * populates; mappers fall back to `amount` when absent).
   */
  net_amount?: string
  /**
   * Seat quantity for the whole order (one count per invoice, never
   * per line item). `Invoice.amount` stays the order total; the backend
   * never multiplies by seats. Optional for fixture back-compat (runtime
   * always populates; treat absent as 1).
   */
  seats?: number
  /**
   * The SR-edited, customer-facing invoice title verbatim — "" until the
   * rep edits it (the backend composes the default on render). Optional
   * for fixture back-compat (runtime always populates).
   */
  title?: string
  course_code: string
  course_name: string
  hubspot_deal_id: string
  hubspot_deal_owner_id: string
  status: BackendStatus
  /**
   * Per-course breakdown in position order. The top-level
   * ``course_code`` / ``course_name`` are the single-label primary
   * mirror. Optional for fixture back-compat.
   */
  line_items?: InvoiceLineItemOut[]
  /**
   * Computed payment mode (derived from the plan FKs). Drives the
   * "Deposit" list-row indicator and the payment-mode filter. Optional
   * for fixture back-compat; runtime always populates it.
   */
  payment_mode?: PaymentMode
  course_start_date: string
  expiration_date: string
  reminder_date: string | null
  course_id: string
  discount_type: string
  discount_value: string | null
  memo: string
  stripe_account: StripeAccount
  currency: string
  market_name: string
  created_at: string
  updated_at: string
  created_by: CreatedByOut
}

export interface InvoiceListResponse {
  items: InvoiceOut[]
  count: number
  limit: number
  offset: number
}

/**
 * The market / currency values present in the caller's visible invoices.
 * Each list-page picker offers exactly these, so it never advertises a
 * value that would return zero rows.
 */
export interface InvoiceFilterFacetsOut {
  /** Raw stored labels, e.g. `["US", "UK", "APAC"]`. */
  markets: string[]
  /** Lowercase ISO-4217 codes, A-Z. */
  currencies: string[]
}

export interface InvoiceFilterParams {
  status?: BackendStatus | BackendStatus[]
  payment_mode?: PaymentMode
  /** Repeated `?market=` values, OR'd server-side (free text). */
  market?: string[]
  /** Lowercase ISO-4217 code. */
  currency?: string
  course_code?: string
  course_id?: string
  hubspot_deal_id?: string
  hubspot_deal_owner_id?: string
  created_by?: number | "me"
  created_at_from?: string
  created_at_to?: string
  updated_at_from?: string
  updated_at_to?: string
  search?: string
  limit?: number
  offset?: number
}

export type InstallmentScheduleRowStatus =
  | "pending"
  | "paid"
  | "awaiting_card_update"
  | "primary_exhausted"
  | "awaiting_auth"
  | "backup_exhausted"
  | "awaiting_auth_backup"
  | "failed"
  | "written_off"

export type InstallmentDeclineCategory =
  | ""
  | "soft"
  | "generic"
  | "card_fatal_expired"
  | "card_fatal_replace"
  | "auth_required"
  | "fraud"

export interface InstallmentScheduleRowOut {
  schedule_id: string
  sequence: number
  due_date: string
  amount: string
  currency: string
  status: InstallmentScheduleRowStatus
  decline_category: InstallmentDeclineCategory
  last_decline_code: string
  last_error_code: string
  fraud_flag: boolean
  retry_count: number
  backup_retry_count: number
  last_payment_intent_id: string
  last_failure_pi_id: string
  next_attempt_after: string | null
  /**
   * Stripe-computed tax for this installment in minor currency units.
   * ``null`` until the row's success webhook fires. Optional for
   * fixture back-compat (runtime always populates).
   */
  tax_minor?: number | null
}

export interface InstallmentPlanOut {
  id: string
  num_installments: number
  cadence: InstallmentCadence
  total_amount: string
  currency: string
  start_date: string
  // Per-plan backup (second) card requirement, snapshotted at plan
  // creation from the global default. Lets the create form restore the
  // "Require backup payment method" checkbox from the plan's stored value
  // when re-opening a DRAFT to edit.
  require_backup_card: boolean
  // Manager pause state (instalments-modification). ``paused_at`` null =>
  // active; set => paused (indefinite when ``resume_on`` null, else
  // auto-resumes on/after ``resume_on``). Optional for back-compat with
  // fixtures predating the field (the mapper defaults absent => null).
  paused_at?: string | null
  resume_on?: string | null
  schedule: InstallmentScheduleRowOut[]
}

export interface InstallmentDefaultsOut {
  // The global installments_require_backup_card operator switch — the
  // value a newly created plan snapshots when the rep leaves the
  // per-invoice checkbox at its default.
  require_backup_card: boolean
}

export interface InstallmentProgressOut {
  paid_count: number
  total_count: number
  next_due_date: string | null
  next_due_amount: string | null
  next_due_sequence: number | null
  current_schedule_status: InstallmentScheduleRowStatus | null
  last_decline_code: string
  last_decline_category: InstallmentDeclineCategory
}

export interface ManualRetryOut {
  schedule_id: string
  invoice_id: string
  outcome: string
  status: InstallmentScheduleRowStatus
  previous_status: InstallmentScheduleRowStatus
  payment_intent_id: string | null
  role: string
  attempt: number
  idempotency_key: string
  updated_at: string
}

export interface InvoiceNamedDiscountOut {
  named_discount_id: string
  name: string
  discount_type: string
  discount_value: string
  position: number
  amount: string
}

/**
 * Marketing-attribution snapshot the server derives from the deal's
 * notes at create time. Display-only: never an editable input, never
 * part of a create/update request body (design §2.3/§5.1). Empty
 * strings represent "no attribution captured".
 */
export interface MarketingOut {
  utm_source: string
  utm_medium: string
  utm_content: string
  landing_slug: string
}

export type DepositStage = "deposit" | "balance"

/**
 * Per-leg lifecycle status. Reuses the shared payment statuses; a leg
 * is ``pending`` until its Checkout settles. The invoice-level status
 * is the aggregate (see the deposit-payments spec §6.4).
 */
export type DepositLegStatus =
  | "pending"
  // The leg was paid with an async method and awaits settlement.
  | "processing"
  | "paid"
  | "payment_failed"
  | "refunded"
  | "partially_refunded"

/** One deposit leg as it appears on the authed detail output. */
export interface DepositPaymentOut {
  stage: DepositStage
  amount: string
  currency: string
  status: DepositLegStatus
  due_date: string
  paid_at: string | null
  payable: boolean
  email_sent_at: string | null
  payment_method_type: string
  payment_method_brand: string
  payment_method_last4: string
  tax_minor: number | null
}

/** The deposit plan block on the authed detail output. */
export interface DepositPlanOut {
  id: string
  total_amount: string
  currency: string
  deposit: DepositPaymentOut
  balance: DepositPaymentOut
}

export interface InvoiceDetailOut {
  id: string
  email: string
  first_name: string
  last_name: string
  phone: string
  amount: string
  /**
   * Discount-inclusive price the customer pays (see `InvoiceOut.net_amount`).
   * Optional for fixture back-compat; mappers fall back to `amount`.
   */
  net_amount?: string
  /**
   * Seat quantity for the whole order. Seeds the edit form's seats input;
   * `amount` stays the order total. Optional for fixture back-compat
   * (runtime always populates; treat absent as 1).
   */
  seats?: number
  /**
   * The SR-edited, customer-facing invoice title verbatim — "" until the
   * rep edits it (the backend composes the default on render). Seeds the
   * edit form's title input. Optional for fixture back-compat (runtime
   * always populates).
   */
  title?: string
  course_code: string
  course_name: string
  course_id: string
  hubspot_deal_id: string
  hubspot_deal_owner_id: string
  stripe_account: StripeAccount
  currency: string
  status: BackendStatus
  course_start_date: string
  expiration_date: string
  reminder_date: string | null
  discount_type: string
  discount_value: string | null
  /**
   * Attached named discounts in cascade order, with their applied
   * amounts. Empty when none attached. Optional for fixture back-compat
   * (runtime always populates).
   */
  named_discounts?: InvoiceNamedDiscountOut[]
  /**
   * Per-course breakdown in position order. The top-level ``course_*``
   * fields stay the single-label primary (position-0) mirror; this is the
   * full list the edit form re-hydrates from. Optional for fixture
   * back-compat (runtime always populates).
   */
  line_items?: InvoiceLineItemOut[]
  memo: string
  market_name: string
  /**
   * Server-derived marketing attribution (design §2.3/§5.1). Display-only;
   * never submitted back. Default "" when the deal carried no marketing
   * note. Optional only on the wire shape for fixture back-compat — the
   * mapper always populates the projection with "" defaults.
   */
  utm_source?: string
  utm_medium?: string
  utm_content?: string
  landing_slug?: string
  /**
   * Raw marker-matching note(s) verbatim, detail-only (audit / re-parse).
   * ``null`` when nothing was fetched. Optional for fixture back-compat.
   */
  marketing_note_snapshot?: Record<string, unknown> | null
  hubspot_deal_snapshot: Record<string, unknown> | null
  pay_page_url: string
  created_at: string
  updated_at: string
  created_by: CreatedByOut
  installment_plan?: InstallmentPlanOut | null
  installment_progress?: InstallmentProgressOut | null
  /**
   * Computed payment mode (derived from the plan FKs). Optional for
   * fixture back-compat; runtime always populates it.
   */
  payment_mode?: PaymentMode
  /**
   * The deposit plan block when this is a deposit invoice; ``null`` /
   * absent otherwise. Parallel to ``installment_plan``.
   */
  deposit_plan?: DepositPlanOut | null
  /**
   * Stripe-computed tax in minor currency units for the one-shot flow.
   * ``null`` on plan invoices (per-row tax lives on the schedule rows)
   * and until the success webhook fires. Admin surface renders ``—``
   * on ``null``. Optional for fixture back-compat (runtime always
   * populates).
   */
  tax_minor?: number | null
}

export interface InstallmentScheduleRow {
  sequence: number
  scheduleId: string
  dueDate: Date
  amount: number
  amountRaw: string
  currency: string
  status: InstallmentScheduleRowStatus
  declineCategory: InstallmentDeclineCategory
  lastDeclineCode: string
  lastErrorCode: string
  fraudFlag: boolean
  retryCount: number
  backupRetryCount: number
  lastPaymentIntentId: string
  lastFailurePaymentIntentId: string
  nextAttemptAfter: Date | null
  /**
   * Stripe-computed tax for this installment in minor currency units.
   * ``null`` until the row's success webhook fires. Optional for
   * fixture back-compat (runtime mappers always populate).
   */
  taxMinor?: number | null
}

export interface InstallmentProgress {
  paidCount: number
  totalCount: number
  nextDueDate: Date | null
  nextDueAmount: number | null
  nextDueAmountRaw: string | null
  nextDueSequence: number | null
  currentScheduleStatus: InstallmentScheduleRowStatus | null
  lastDeclineCategory: InstallmentDeclineCategory
  lastDeclineCode: string
}

export interface InstallmentPlan {
  id: string
  numInstallments: number
  cadence: InstallmentCadence
  totalAmount: number
  totalAmountRaw: string
  currency: string
  startDate: Date
  // Per-plan backup (second) card requirement, restored into the create
  // form's checkbox when re-opening a DRAFT to edit.
  requireBackupCard: boolean
  // Manager pause state (instalments-modification). ``pausedAt`` null =>
  // active; set => paused (indefinite when ``resumeOn`` null, else
  // auto-resumes on/after ``resumeOn``). Always populated by the mapper
  // (null when absent); optional only for back-compat with older fixtures.
  pausedAt?: Date | null
  resumeOn?: Date | null
  schedule: InstallmentScheduleRow[]
}

/** Frontend-shaped deposit leg (dates parsed, amount numeric). */
export interface DepositLeg {
  stage: DepositStage
  amount: number
  amountRaw: string
  currency: string
  status: DepositLegStatus
  dueDate: Date
  paidAt: Date | null
  payable: boolean
  emailSentAt: Date | null
  paymentMethodType: string
  paymentMethodBrand: string
  paymentMethodLast4: string
  taxMinor: number | null
}

/** Frontend-shaped deposit plan. */
export interface DepositPlan {
  id: string
  totalAmount: number
  totalAmountRaw: string
  currency: string
  deposit: DepositLeg
  balance: DepositLeg
}

/**
 * Frontend-shaped per-course line item (multiple-courses-in-invoice).
 * The edit form re-hydrates its rows from this list: ``unitPrice`` is the
 * **per-seat** price in the issue currency (used to seed the row's Price
 * field — the field is the per-seat unit, the backend derives the total),
 * ``amount`` is the per-course TOTAL (= ``round(unitPrice × seats)``), and
 * ``baseCurrency`` is the course's pre-conversion currency.
 */
export interface InvoiceLineItem {
  position: number
  courseId: string
  courseCode: string
  /** Effective customer-facing name (the SR override or the snapshot). */
  courseName: string
  /** Pristine Planna snapshot — the "Reset to default" baseline. */
  courseNameDefault: string
  cohortId: number | null
  courseStartDate: Date
  /**
   * Per-seat unit price (issue currency). Seeds the row's Price input.
   * Falls back to ``amount`` when the wire omits it (legacy fixtures).
   */
  unitPrice: string
  amount: string
  baseCurrency: string
  marketName: string
  instructorName: string
  instructorPhotoUrl: string
  fullPrice: string | null
  tierIndex: number | null
}

export interface InvoiceDetail {
  id: string
  invoiceNumber: string
  status: BackendStatus
  customer: {
    name: string
    email: string
  }
  firstName: string
  lastName: string
  email: string
  phone: string
  dealId: string
  courseId: string
  course: string
  courseCode: string
  amount: number
  /**
   * Seat quantity for the whole order. Seeds the edit form's seats input
   * (as a string). `amount` stays the order total. Defaults to 1 for
   * fixture back-compat (the mapper always populates it at runtime).
   */
  seats?: number
  /**
   * The SR-edited, customer-facing invoice title verbatim ("" when never
   * edited — the backend composes the default on render). Seeds the edit
   * form's title input and freezes its reactive default. Optional for
   * fixture back-compat (the mapper always populates it at runtime).
   */
  title?: string
  price: string
  currency: string
  stripeAccount: StripeAccount
  discount: { type: DiscountType; value: string } | null
  memo: string
  createdAt: Date
  updatedAt: Date
  deadline: Date | null
  reminderDate: Date | null
  courseStartDate: Date
  expirationDate: Date
  /**
   * Per-course breakdown in position order (multiple-courses). The
   * top-level ``courseId`` / ``course`` / ``courseCode`` / ``price`` fields
   * stay the single-label primary (position-0) mirror; the edit form
   * re-hydrates its repeatable rows from this list. The mapper always
   * populates it at runtime (synthesizing a single row from the primary
   * mirror for legacy fixtures); optional only on the type so the many
   * hand-built test fixtures stay terse, mirroring ``namedDiscountIds?``.
   */
  lineItems?: InvoiceLineItem[]
  hubspotDealUrl?: string
  createdBy: { id: number; email: string; display_name: string }
  installmentPlan: InstallmentPlan | null
  installmentProgress: InstallmentProgress | null
  /**
   * Which payment mode this invoice was created with. Drives the
   * create-form picker seed and the detail-page "Deposit" badge.
   */
  paymentMode: PaymentMode
  /**
   * The deposit plan when this is a deposit invoice, else ``null``.
   * Parallel to ``installmentPlan``.
   */
  depositPlan: DepositPlan | null
  /**
   * Cohort's Planna market snapshot at the last persist (slice 03
   * country gate). The empty string represents legacy rows that
   * landed before the snapshot column was populated.
   */
  marketName: string
  /**
   * Attached named-discount catalog ids in cascade (position) order.
   * Seeds the edit form's multi-select; empty when none attached. Optional
   * for fixture back-compat (the mapper always populates it at runtime).
   */
  namedDiscountIds?: string[]
  /**
   * Snapshot display name for each attached named discount, keyed by its
   * catalog id. Lets the edit form label (and therefore remove) an
   * attached discount that has since been deactivated and dropped out of
   * the active catalog. Optional for fixture back-compat (the mapper
   * always populates it at runtime, empty when none attached).
   */
  namedDiscountMeta?: Record<string, string>
  /**
   * Server-derived marketing attribution (design §2.3/§5.1). Display-only —
   * rendered as the read-only "Attribution" section, never an editable
   * field and never part of any update payload. The mapper always
   * populates this with "" defaults (fixture back-compat).
   */
  marketing: {
    utmSource: string
    utmMedium: string
    utmContent: string
    landingSlug: string
  }
}

export type DiscountType = "percent" | "fixed"

export interface DiscountIn {
  type: DiscountType
  value: string
}

/** One row in the `GET /api/named-discounts/` picker feed. */
export interface NamedDiscountOut {
  id: string
  name: string
  discount_type: DiscountType
  discount_value: string
}

export interface NamedDiscountListResponse {
  items: NamedDiscountOut[]
  count: number
}

/**
 * One course on a create/edit request (standalone API).
 *
 * ``price`` is quoted in the course's base currency and converted
 * server-side into the invoice ``currency``. Each course carries **exactly
 * one** start-date source: ``cohort_id`` (the SR-selected cohort, whose
 * start date the server uses) or ``course_start_date`` (an SR-entered
 * ``YYYY-MM-DD`` date for a course with no cohort). ``base_currency`` is an
 * optional per-course override, sent only when neither the course nor the
 * cohort carries a currency and the SR picked the base manually.
 */
export interface LineItemIn {
  course_id: string
  cohort_id?: number
  course_start_date?: string
  price: string
  base_currency?: string
  // SR override for the customer-facing course display name. Sent only when
  // the row is overridden (the typed name differs from the Planna default);
  // omitting it is how "no override / reset to default" is expressed on the
  // wire. A present-but-blank value is a 422 (the FE blocks it first).
  course_name?: string
}

export interface CreateInvoiceIn {
  first_name: string
  last_name: string
  email: string
  phone?: string
  deal_id: string
  // One entry per course (>=1). The server converts each line's price
  // into ``currency`` and derives the invoice's earliest-start anchor
  // from the per-course cohorts, so ``course_start_date`` is no longer in
  // the request.
  line_items: LineItemIn[]
  // Seat quantity for the whole order (>=1). The invoice amount stays the
  // order total; the backend never multiplies by seats. Optional only for
  // fixture back-compat — the mapper always sends it.
  seats?: number
  // SR-composed customer-facing title. The form always sends it; a blank
  // string tells the backend to compose the default on render. Optional
  // only for fixture back-compat — the mapper always sends it.
  title?: string
  currency: string
  discount?: DiscountIn | null
  // Catalog ids to attach as named discounts, in cascade order. Omitted
  // when none are selected.
  named_discount_ids?: string[]
  payment_deadline: string
  memo?: string | null
  // Always sent (default "full"). count/cadence are sent only when
  // payment_mode === "installments"; deposit needs no extra inputs.
  payment_mode: PaymentMode
  installment_count?: number | null
  installment_cadence?: InstallmentCadence | null
  // Per-invoice backup (second) card requirement; only meaningful for
  // payment_mode === "installments". Omitted otherwise. null/omitted lets
  // the backend snapshot the global default.
  require_backup_card?: boolean | null
  // Rep-editable balance (second) payment deadline; only meaningful for
  // payment_mode === "deposit". Omitted when empty (balance leg defaults to
  // the invoice expiration date). Must be >= payment_deadline.
  deposit_balance_deadline?: string | null
}

export interface UpdateInvoiceIn {
  first_name?: string
  last_name?: string
  email?: string
  // Omit to leave untouched; "" clears the stored phone.
  phone?: string
  deal_id?: string
  // Omit to leave the courses untouched; present (>=1) wholesale-replaces
  // the course list. A currency change MUST be accompanied by line_items
  // (the backend 422s a currency-only patch without them).
  line_items?: LineItemIn[]
  // Omit to leave the seat count untouched; present (>=1) replaces it.
  seats?: number
  // Omit to leave the title untouched; present replaces it ("" clears it
  // back to the composed default). The mapper emits it only when changed.
  title?: string
  currency?: string
  discount?: DiscountIn | null
  // Omit to leave attachments untouched; present (incl. []) replaces the
  // attached set.
  named_discount_ids?: string[]
  payment_deadline?: string
  memo?: string | null
  // Present only when the payment mode (or its installment sub-fields)
  // changed. count/cadence accompany it only when switching to/within
  // "installments"; "deposit"/"full" send payment_mode alone.
  payment_mode?: PaymentMode
  installment_count?: number | null
  installment_cadence?: InstallmentCadence | null
  // Per-plan backup (second) card requirement. Omit to leave the plan's
  // stored value untouched; a bool persists it onto the installment plan
  // (even on a count/cadence-unchanged toggle). Emitted only when the
  // result is installments and the value changed.
  require_backup_card?: boolean | null
  // Balance (second) payment deadline for a deposit invoice. Omit to leave
  // it unchanged; a date sets it; null resets it to the invoice expiration
  // date (tri-state, mirroring the backend PATCH sentinel).
  deposit_balance_deadline?: string | null
}

export interface EmailPreviewOut {
  html: string
  pay_url: string
  pdf_url: string
}

export interface VoidInvoiceIn {
  reason: string
}

/**
 * Body for ``POST /api/invoices/{id}/amend/`` — the discounts-only amend
 * of a sent-but-unpaid invoice. Both fields are optional and follow the
 * update omission semantics: omit a key to leave it untouched, send
 * ``discount: null`` to clear the standard discount, send
 * ``named_discount_ids`` (incl. ``[]``) to replace the attached set. The
 * server rejects a body with no effective change (422).
 */
export interface AmendInvoiceIn {
  discount?: DiscountIn | null
  named_discount_ids?: string[]
  // ISO ``YYYY-MM-DD`` payment deadline; omit to leave it unchanged. No
  // null — the backend rejects clearing the deadline (422).
  payment_deadline?: string
  // ISO ``YYYY-MM-DD`` deposit balance (second-payment) deadline; only for
  // deposit invoices. Omit = leave unchanged; a date sets it; null resets it
  // to the invoice expiration date. Must be >= payment_deadline.
  deposit_balance_deadline?: string | null
  // Whether the customer is emailed the "invoice updated" notification.
  // Omit = server default (true); the edit form always sends it explicitly.
  send_email?: boolean
}

export interface DealSearchParams {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  limit?: number
}

export interface DealSearchHitOut {
  deal_id: string
  // HubSpot's standard `dealname`. Rendered as the card title; ``null``
  // when HubSpot omits it.
  deal_name: string | null
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  course_name: string | null
  // HubSpot deal-owner display name, resolved live from
  // ``hubspot_owner_id``. ``null`` when the deal is unassigned or the
  // owner lookup fails.
  owner_name: string | null
  // HubSpot's createdate. ISO-8601 UTC, may be ``null`` for legacy deals.
  created_at: string | null
}

export interface DealSearchOut {
  items: DealSearchHitOut[]
  has_more: boolean
}

// Field-for-field mirrors of the extension prefill projections.
// Re-declared here so the standalone surface stays insulated from
// future extension-only changes.
export interface PrefillDealOut {
  id: string
  amount: string | null
  currency: string | null
  course_code: string | null
  course_name: string | null
}

export interface PrefillContactOut {
  id: string
  email: string | null
  first_name: string | null
  last_name: string | null
  phone: string | null
}

export interface PrefillCourseOut {
  id: number
  code: string
  name: string
  currency: string | null
  default_avg_price: string | null
}

export interface PrefillDatesOut {
  course_start_date: string | null
  reminder_date: string | null
  start_dates: string[]
  // Cohort the picker should pre-select on a tier-1 cohort-code hit; null
  // for every other tier (the form then defaults to the earliest start
  // date). Also forwarded as `include_cohort_id` to the cohorts endpoint
  // so a past coded cohort stays in the option list
  // (cohorts-search-by-hubspot-code §3.2, D8). Optional for back-compat
  // with any prefill payload minted before this field existed.
  selected_cohort_id?: number | null
}

export interface DealPrefillOut {
  deal: PrefillDealOut
  contact: PrefillContactOut | null
  course: PrefillCourseOut | null
  dates: PrefillDatesOut
  // Server-derived attribution echoed read-only so reps can see what
  // will be captured at create (design §2.3). Display-only; the client
  // never posts it back. Values may be empty strings.
  marketing: MarketingOut
  cache_source: "live" | "cache"
  warnings: string[]
  // settings.INVOICE_EXPIRATION_GRACE_DAYS, echoed so the create form can
  // compute the default deposit balance deadline as earliestStart + grace
  // without hardcoding the value (deposits-deadline-edit §5.2).
  invoice_expiration_grace_days: number
}


export type DiscountMode = "none" | DiscountType
// The SR-selected issue currency: any lowercase payable ISO code from
// the picker (catalogue ∩ backend allowlist), or "" before a pick.
// Widened from the old "usd"/"gbp"-only union for multi-currency
// issuance.
export type CurrencyChoice = string
export type InstallmentCadence = "monthly" | "biweekly"
export type InstallmentCountOption = "2" | "3" | "4"

/**
 * One repeatable course row in the create/edit form (multiple-courses).
 *
 * Mirrors the per-course inputs the form used to carry as singletons,
 * now repeated per course via react-hook-form's ``useFieldArray``. The
 * ``price`` is entered in the course's base currency; the issue currency
 * stays invoice-level. ``cohortId`` is the SR-selected start date for this
 * course (``null`` until the cohort picker emits). ``baseCurrency`` is the
 * optional per-course base-currency override.
 */
export interface LineItemForm {
  courseId: string
  cohortId: number | null
  price: string
  baseCurrency: CurrencyChoice
  /**
   * SR-editable customer-facing course display name (the effective name).
   * Seeded from the picked course's title and latched once the rep edits;
   * the mapper sends it as ``course_name`` only when it differs from
   * ``courseNameDefault`` (an override). Never allowed empty — the schema
   * blocks a cleared box with a required-field error.
   */
  courseName: string
  /**
   * The Planna course title — the reset baseline. Kept in sync with the
   * resolved course; not sent to the backend (the server re-resolves it).
   */
  courseNameDefault: string
  /**
   * This row's selected cohort start date (``YYYY-MM-DD``, or "" before a
   * pick). Kept per-row so the form can derive the invoice's earliest
   * start (which drives the deadline / date validation) and so the cohort
   * picker can re-hydrate its selection on edit. Not sent to the backend
   * — the server derives ``course_start_date`` from the cohort.
   */
  startDate: string
}

export interface CreateInvoiceForm {
  firstName: string
  lastName: string
  email: string
  phone: string
  dealId: string
  // One row per course (>=1). Each carries its own course/cohort/price;
  // the issue ``currency`` below stays invoice-level (one issue currency
  // for the whole invoice).
  lineItems: LineItemForm[]
  // Seat quantity for the whole order (one count per invoice, never
  // per line item). A text-input value parsed to a whole int >= 1 at
  // submit; the validation lives in the schema.
  seats: string
  // SR-editable customer-facing title (headline on the pay page +
  // emails). Reactively defaulted to the composed
  // "<course names> x N (mark)" string while pristine; frozen once the
  // rep edits it or when seeded from a persisted draft. Sent verbatim;
  // blank lets the backend compose the default.
  title: string
  currency: CurrencyChoice
  discountMode: DiscountMode
  discountValue: string
  // Catalog ids of attached named discounts, in cascade (selection)
  // order. Empty when none selected.
  namedDiscountIds: string[]
  paymentDeadline: string
  memo: string
  // The selected payment mode (full / installments / deposit / on_call).
  // installmentCount/installmentCadence apply only when paymentMode ===
  // "installments". ``on_call`` is a create-only channel that maps to a
  // one-time ``full`` invoice charged on the call (on-call-payment design).
  paymentMode: CreateInvoicePaymentMode
  installmentCount: InstallmentCountOption
  installmentCadence: InstallmentCadence
  // The per-invoice backup (second) card requirement, shown only in
  // installments mode. Defaults to the global installment switch (via
  // useInstallmentDefaults) on the create path, or the plan's stored value
  // on the reissue/edit path.
  requireBackupCard: boolean
  // The rep-editable balance (second) payment deadline (ISO date), shown
  // only in deposit mode. Empty = no override (backend defaults the balance
  // leg to the invoice expiration date). Pre-filled to earliestStart + grace
  // on the create form; restored from the persisted draft on the edit form.
  depositBalanceDeadline: string
}
